import { NodeIdAttrName, NodeIdMissingValue, NodeIdRootValue } from '@common/prosemirror/model/node-id';
import { SchemaPlugin } from '@common/prosemirror/model/schema-plugin';
import { OperationAttrName } from '@common/prosemirror/operations/operations.constant';
import { NodeSpec } from 'prosemirror-model';

const groupName = 'block';

/** CSS node names. */
export enum CssNodeName {
  root = 'root',
  atRule = 'atrule',
  rule = 'rule',
  declaration = 'decl',
  comment = 'comment',
}

/** CSS nodes attribute names. */
export enum CssAttributeName {
  type = 'type',
  after = 'after',
  afterName = 'afterName',
  before = 'before',
  between = 'between',
  codeAfter = 'codeAfter',
  codeBefore = 'codeBefore',
  left = 'left',
  right = 'right',
  semicolon = 'semicolon',
  ownSemicolon = 'ownSemicolon',
  name = 'name',
  params = 'params',
  selector = 'selector',
  prop = 'prop',
  value = 'value',
  important = 'important',
  variable = 'variable',
  text = 'text',
  inline = 'inline',
  importantRawValue = 'importantRawValue',
  paramsRaw = 'paramsRaw',
  paramsRawValue = 'paramsRawValue',
  selectorRaw = 'selectorRaw',
  selectorRawValue = 'selectorRawValue',
  valueRaw = 'valueRaw',
  valueRawValue = 'valueRawValue',
}

/** Prosemirror document schema plugin for CSS format.
 * This schema corresponds to post-css AST nodes structure.
 * The AST Node.raws properties mapped to the prosemirror nodes attributes.
*/
export class CssSchemaPlugin extends SchemaPlugin {
  nodes: Dictionary<NodeSpec> = {
    doc: {
      content: CssNodeName.root,
    },
    [CssNodeName.root]: {
      content: 'block*',
      attrs: {
        [NodeIdAttrName]: { default: NodeIdRootValue, skipExport: true },
        [OperationAttrName]: { default: undefined, skipExport: true },
        [CssAttributeName.type]: { default: undefined, skipExport: true, minor: true },
        [CssAttributeName.after]: { default: undefined, minor: true },
        [CssAttributeName.codeAfter]: { default: undefined, minor: true },
        [CssAttributeName.codeBefore]: { default: undefined, minor: true },
        [CssAttributeName.semicolon]: { default: undefined, minor: true },
      },
    },
    [CssNodeName.atRule]: {
      group: groupName,
      content: 'block*',
      attrs: {
        [NodeIdAttrName]: { default: NodeIdMissingValue, skipExport: true },
        [CssAttributeName.name]: { default: undefined },
        [CssAttributeName.params]: { default: undefined },
        [CssAttributeName.after]: { default: undefined, minor: true },
        [CssAttributeName.afterName]: { default: undefined, minor: true },
        [CssAttributeName.before]: { default: undefined, minor: true },
        [CssAttributeName.between]: { default: undefined, minor: true },
        [CssAttributeName.ownSemicolon]: { default: undefined, minor: true },
        [CssAttributeName.semicolon]: { default: undefined, minor: true },
        [CssAttributeName.paramsRaw]: { default: undefined, minor: true },
        [CssAttributeName.paramsRawValue]: { default: undefined, minor: true },
      },
    },
    [CssNodeName.rule]: {
      group: groupName,
      content: 'block*',
      attrs: {
        [NodeIdAttrName]: { default: NodeIdMissingValue, skipExport: true },
        [CssAttributeName.selector]: { default: undefined },
        [CssAttributeName.after]: { default: undefined, minor: true },
        [CssAttributeName.before]: { default: undefined, minor: true },
        [CssAttributeName.between]: { default: undefined, minor: true },
        [CssAttributeName.ownSemicolon]: { default: undefined, minor: true },
        [CssAttributeName.semicolon]: { default: undefined, minor: true },
        [CssAttributeName.selectorRaw]: { default: undefined, minor: true },
        [CssAttributeName.selectorRawValue]: { default: undefined, minor: true },
      },
    },
    [CssNodeName.declaration]: {
      group: groupName,
      attrs: {
        [NodeIdAttrName]: { default: NodeIdMissingValue, skipExport: true },
        [CssAttributeName.prop]: { default: undefined, isKey: true },
        [CssAttributeName.value]: { default: undefined },
        [CssAttributeName.important]: { default: undefined },
        [CssAttributeName.variable]: { default: undefined },
        [CssAttributeName.before]: { default: undefined, minor: true },
        [CssAttributeName.between]: { default: undefined, minor: true },
        [CssAttributeName.importantRawValue]: { default: undefined, minor: true },
        [CssAttributeName.valueRaw]: { default: undefined, minor: true },
        [CssAttributeName.valueRawValue]: { default: undefined, minor: true },
      },
    },
    [CssNodeName.comment]: {
      group: groupName,
      attrs: {
        [NodeIdAttrName]: { default: NodeIdMissingValue, skipExport: true },
        [CssAttributeName.text]: { default: undefined },
        [CssAttributeName.before]: { default: undefined, minor: true },
        [CssAttributeName.left]: { default: undefined, minor: true },
        [CssAttributeName.right]: { default: undefined, minor: true },
        [CssAttributeName.inline]: { default: undefined, minor: true },
      },
    },
  };
}
